'use client'

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { pushBackHandler } from '@/lib/mobileBackStack'

interface ModalInfo {
  id: string
  position: 'center' | 'left' | 'right' | 'inpopup'
  onRequestClose?: () => void
}

interface ModalContextType {
  modals: ModalInfo[]
  registerModal: (id: string, position: string, onRequestClose?: () => void) => void
  unregisterModal: (id: string) => void
  finalizeClose: (id: string) => void
  getZIndex: (id: string) => number
  getModalIndex: (id: string, position: string) => number
  getModalCountByPosition: (position: string) => number
  isTopModal: (id: string) => boolean
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

// Map modal id → remove function from mobileBackStack
const backHandlerCleanups = new Map<string, () => void>()

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modals, setModals] = useState<ModalInfo[]>([])
  // renderingCount tracks modals that are still visible (including close animation)
  // used for scroll lock so body doesn't unlock while a modal is still animating out
  const [renderingCount, setRenderingCount] = useState(0)

  const registerModal = useCallback((id: string, position: string, onRequestClose?: () => void) => {
    setModals(prev => [...prev, { id, position: position as any, onRequestClose }])
    setRenderingCount(prev => prev + 1)

    // Push onto centralized back-button stack
    if (onRequestClose && typeof window !== 'undefined' && window.innerWidth <= 768) {
      const remove = pushBackHandler(onRequestClose)
      backHandlerCleanups.set(id, remove)
    }
  }, [])

  // Called immediately when isOpen becomes false — removes from stacking so other modals revert push offset
  const unregisterModal = useCallback((id: string) => {
    setModals(prev => prev.filter(m => m.id !== id))

    // Clean up back-button handler
    const cleanup = backHandlerCleanups.get(id)
    if (cleanup) {
      cleanup()
      backHandlerCleanups.delete(id)
    }
  }, [])

  // Called when close animation finishes (shouldRender=false) — releases scroll lock
  const finalizeClose = useCallback((_id: string) => {
    setRenderingCount(prev => Math.max(0, prev - 1))
  }, [])

  const getZIndex = useCallback((id: string) => {
    const index = modals.findIndex(m => m.id === id)
    return 10000 + (index * 10)
  }, [modals])

  const getModalIndex = useCallback((id: string, position: string) => {
    // Get index only within modals of the same position
    const samePositionModals = modals.filter(m => m.position === position)
    return samePositionModals.findIndex(m => m.id === id)
  }, [modals])

  const getModalCountByPosition = useCallback((position: string) => {
    return modals.filter(m => m.position === position).length
  }, [modals])

  const isTopModal = useCallback((id: string) => {
    return modals[modals.length - 1]?.id === id
  }, [modals])

  // Body overflow management (+ scrollbar compensation to avoid layout shift)
  // Based on renderingCount (not modals.length) so scroll lock stays during close animation
  useEffect(() => {
    const body = document.body
    const root = document.documentElement

    const lockKey = 'modalScrollLock'
    const prevOverflowKey = 'modalPrevOverflow'
    const prevPaddingKey = 'modalPrevPaddingRight'

    if (renderingCount > 0) {
      // Capture previous styles only once (first modal)
      if (body.dataset[lockKey] !== '1') {
        body.dataset[lockKey] = '1'
        body.dataset[prevOverflowKey] = body.style.overflow || ''
        body.dataset[prevPaddingKey] = body.style.paddingRight || ''
      }

      const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth)
      body.style.overflow = 'hidden'
      body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : (body.dataset[prevPaddingKey] || '')
    } else {
      if (body.dataset[lockKey] === '1') {
        body.style.overflow = body.dataset[prevOverflowKey] || ''
        body.style.paddingRight = body.dataset[prevPaddingKey] || ''
        delete body.dataset[lockKey]
        delete body.dataset[prevOverflowKey]
        delete body.dataset[prevPaddingKey]
      } else {
        body.style.overflow = ''
        body.style.paddingRight = ''
      }
    }

    return () => {
      // Provider unmount safety
      try {
        if (body.dataset[lockKey] === '1') {
          body.style.overflow = body.dataset[prevOverflowKey] || ''
          body.style.paddingRight = body.dataset[prevPaddingKey] || ''
          delete body.dataset[lockKey]
          delete body.dataset[prevOverflowKey]
          delete body.dataset[prevPaddingKey]
        }
      } catch {
        // ignore
      }
    }
  }, [renderingCount])

  return (
    <ModalContext.Provider value={{ modals, registerModal, unregisterModal, finalizeClose, getZIndex, getModalIndex, getModalCountByPosition, isTopModal }}>
      {children}
    </ModalContext.Provider>
  )
}

export function useModalContext() {
  const context = useContext(ModalContext)
  return context // Return undefined if not wrapped in ModalProvider
}
